import type { DateRange } from '@daypicker/react'
import {
  differenceInCalendarDays,
  endOfDay,
  format,
  formatISO,
  isValid,
  parse,
  startOfDay,
  subDays,
  subHours,
} from 'date-fns'

export type PresetKey = 'today' | 'last24h' | 'last7d' | 'custom'

/** Converts a toolbar preset into the ISO range the activity endpoint wants.
 *  A "custom" preset with nothing picked yet falls back to today, and callers
 *  are expected to skip the query in that state rather than rely on the
 *  fallback (see `hasUsableRange` in both consumers). */
export function rangeForPreset(
  preset: PresetKey,
  custom: DateRange | undefined,
): { start: string; end: string } {
  const now = new Date()
  if (preset === 'last24h') return { start: formatISO(subHours(now, 24)), end: formatISO(now) }
  if (preset === 'last7d') return { start: formatISO(subDays(now, 7)), end: formatISO(now) }
  if (preset === 'custom' && custom?.from) {
    return {
      start: formatISO(startOfDay(custom.from)),
      end: formatISO(endOfDay(custom.to ?? custom.from)),
    }
  }
  return { start: formatISO(startOfDay(now)), end: formatISO(now) }
}

const SEARCH_DATE = /^\d{4}-\d{2}-\d{2}$/

/** A `yyyy-MM-dd` URL date that is also a real calendar date. `parse` would
 *  roll 2026-02-31 into March, so the round-trip check is what rejects it. */
export function isSearchDate(value: unknown): value is string {
  if (typeof value !== 'string' || !SEARCH_DATE.test(value)) return false
  const date = parse(value, 'yyyy-MM-dd', new Date())
  return isValid(date) && format(date, 'yyyy-MM-dd') === value
}

/** URL dates → picker range, as LOCAL calendar dates (not UTC midnight), so a
 *  link opens on the same days `rangeForPreset` will bound. */
export function dateRangeFromSearch(from?: string, to?: string): DateRange | undefined {
  if (!isSearchDate(from)) return undefined
  const fromDate = parse(from, 'yyyy-MM-dd', new Date())
  const toDate = isSearchDate(to) ? parse(to, 'yyyy-MM-dd', new Date()) : fromDate
  return { from: fromDate, to: toDate }
}

export function searchFromDateRange(range: DateRange | undefined): { from?: string; to?: string } {
  if (!range?.from) return { from: undefined, to: undefined }
  return { from: format(range.from, 'yyyy-MM-dd'), to: format(range.to ?? range.from, 'yyyy-MM-dd') }
}

/** Inclusive calendar days, matching `rangeForPreset`'s startOfDay/endOfDay
 *  bounds: 7 picked days span 7 days minus 1 ms, inside a 7-day API cap. */
export function customRangeWithinCap(range: DateRange | undefined, maxDays: number): boolean {
  if (!range?.from) return false
  return differenceInCalendarDays(range.to ?? range.from, range.from) + 1 <= maxDays
}
